import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FACTOR_COLORS = ['#2563eb', '#16a34a', '#f59e0b'];

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Init. Visit',
  mosquitoTickControl: 'Mosquito/Tick',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  chartRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  mainSection: {
    flex: '1 1 480px',
    minWidth: '480px',
  },
  sideSection: {
    flex: '0 0 200px',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  container: {
    overflowX: 'auto',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '2px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  totalVariance: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#f9fafb',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#6b7280',
  },
};

export default function FactorLoadingsChart({ data }) {
  const loadingsSvgRef = useRef(null);
  const communalitySvgRef = useRef(null);
  const screeSvgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const factors = useMemo(() => {
    if (!data || !data.factors) return [];
    return data.factors;
  }, [data]);

  const loadings = useMemo(() => {
    if (!data || !data.loadings) return [];
    return data.loadings;
  }, [data]);

  const communalities = useMemo(() => {
    if (!data || !data.communalities) return [];
    return data.communalities;
  }, [data]);

  // Main loadings chart — grouped horizontal bars centered at zero
  useEffect(() => {
    if (loadings.length === 0 || factors.length === 0) return;

    const svg = d3.select(loadingsSvgRef.current);
    svg.selectAll('*').remove();

    const width = 600;
    const height = loadings.length * 60 + 60;
    const margin = { top: 30, right: 20, bottom: 30, left: 110 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Scales
    const fields = loadings.map((d) => d.field);
    const y0 = d3.scaleBand()
      .domain(fields)
      .range([0, innerH])
      .paddingInner(0.25)
      .paddingOuter(0.1);

    const factorIds = factors.map((_, i) => i);
    const y1 = d3.scaleBand()
      .domain(factorIds)
      .range([0, y0.bandwidth()])
      .padding(0.08);

    // Find max absolute loading for symmetric x domain
    let maxAbs = 0;
    loadings.forEach((d) => {
      d.factors.forEach((v) => {
        if (Math.abs(v) > maxAbs) maxAbs = Math.abs(v);
      });
    });
    maxAbs = Math.max(maxAbs, 0.1);
    const xPad = maxAbs * 0.15;

    const x = d3.scaleLinear()
      .domain([-(maxAbs + xPad), maxAbs + xPad])
      .range([0, innerW]);

    // Grid lines — vertical
    g.selectAll('.grid-v')
      .data(x.ticks(8))
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    g.append('line')
      .attr('x1', x(0))
      .attr('x2', x(0))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');

    // Y axis — field labels
    g.append('g')
      .call(d3.axisLeft(y0).tickSize(0).tickFormat((d) => FIELD_LABELS[d] || d))
      .call((axis) => axis.select('.domain').remove())
      .selectAll('text')
      .attr('font-size', '12px')
      .attr('font-weight', 500)
      .attr('fill', '#374151');

    // X axis — top
    g.append('g')
      .call(d3.axisTop(x).ticks(8).tickSize(0).tickFormat((d) => d.toFixed(1)))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis — bottom
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).ticks(8).tickSize(0).tickFormat((d) => d.toFixed(1)))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Draw bars
    loadings.forEach((fieldData) => {
      const fieldY = y0(fieldData.field);
      const communality = communalities.find((c) => c.field === fieldData.field);

      fieldData.factors.forEach((loading, fi) => {
        if (fi >= factors.length) return;

        const barY = fieldY + y1(fi);
        const barH = y1.bandwidth();
        const barX = loading >= 0 ? x(0) : x(loading);
        const barW = Math.abs(x(loading) - x(0));

        g.append('rect')
          .attr('x', barX)
          .attr('y', barY)
          .attr('width', Math.max(0, barW))
          .attr('height', barH)
          .attr('fill', FACTOR_COLORS[fi % FACTOR_COLORS.length])
          .attr('rx', 2)
          .attr('opacity', 0.85)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            d3.select(event.currentTarget).attr('opacity', 1);
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              field: FIELD_LABELS[fieldData.field] || fieldData.field,
              factor: factors[fi].label,
              loading: loading,
              allLoadings: fieldData.factors.map((v, i) => ({
                label: factors[i] ? factors[i].label : `Factor ${i + 1}`,
                value: v,
                color: FACTOR_COLORS[i % FACTOR_COLORS.length],
              })),
              communality: communality ? communality.value : null,
            });
          })
          .on('mouseleave', (event) => {
            d3.select(event.currentTarget).attr('opacity', 0.85);
            setTooltip(null);
          });

        // Loading value label — only if bar wide enough
        if (barW > 25) {
          const labelX = loading >= 0
            ? barX + barW / 2
            : barX + barW / 2;
          g.append('text')
            .attr('x', labelX)
            .attr('y', barY + barH / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .attr('font-size', '10px')
            .attr('font-weight', 600)
            .attr('fill', '#fff')
            .style('pointer-events', 'none')
            .text(loading.toFixed(2));
        }
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [loadings, factors, communalities]);

  // Communality bars — small horizontal bar chart
  useEffect(() => {
    if (communalities.length === 0) return;

    const svg = d3.select(communalitySvgRef.current);
    svg.selectAll('*').remove();

    const width = 200;
    const height = communalities.length * 36 + 30;
    const margin = { top: 16, right: 40, bottom: 14, left: 80 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const fields = communalities.map((d) => d.field);
    const y = d3.scaleBand()
      .domain(fields)
      .range([0, innerH])
      .paddingInner(0.3)
      .paddingOuter(0.1);

    const x = d3.scaleLinear()
      .domain([0, 1])
      .range([0, innerW]);

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0).tickFormat((d) => FIELD_LABELS[d] || d))
      .call((axis) => axis.select('.domain').remove())
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('font-weight', 500)
      .attr('fill', '#6b7280');

    // Background track
    communalities.forEach((d) => {
      g.append('rect')
        .attr('x', 0)
        .attr('y', y(d.field))
        .attr('width', innerW)
        .attr('height', y.bandwidth())
        .attr('fill', '#f3f4f6')
        .attr('rx', 3);
    });

    // Value bars
    communalities.forEach((d) => {
      const barW = x(d.value);
      const color = d.value >= 0.8 ? '#16a34a' : d.value >= 0.6 ? '#f59e0b' : '#dc2626';

      g.append('rect')
        .attr('x', 0)
        .attr('y', y(d.field))
        .attr('width', Math.max(0, barW))
        .attr('height', y.bandwidth())
        .attr('fill', color)
        .attr('rx', 3)
        .attr('opacity', 0.8)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget).attr('opacity', 1);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            field: FIELD_LABELS[d.field] || d.field,
            communality: d.value,
            communalityOnly: true,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget).attr('opacity', 0.8);
          setTooltip(null);
        });

      // Value label
      g.append('text')
        .attr('x', Math.max(0, barW) + 4)
        .attr('y', y(d.field) + y.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', color)
        .style('pointer-events', 'none')
        .text(d.value.toFixed(2));
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [communalities]);

  // Scree-like mini chart — variance explained per factor
  useEffect(() => {
    if (factors.length === 0) return;

    const svg = d3.select(screeSvgRef.current);
    svg.selectAll('*').remove();

    const width = 200;
    const height = 120;
    const margin = { top: 16, right: 16, bottom: 24, left: 40 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const x = d3.scaleBand()
      .domain(factors.map((_, i) => i))
      .range([0, innerW])
      .paddingInner(0.3)
      .paddingOuter(0.2);

    const yMax = Math.max(d3.max(factors, (d) => d.varianceExplained) || 0.1, 0.1);
    const y = d3.scaleLinear()
      .domain([0, yMax * 1.2])
      .range([innerH, 0]);

    // Grid
    g.selectAll('.grid-h')
      .data(y.ticks(4))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((i) => factors[i] ? `F${factors[i].id}` : ''))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(4).tickSize(0).tickFormat((d) => `${(d * 100).toFixed(0)}%`))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '9px')
      .attr('fill', '#6b7280');

    // Bars
    factors.forEach((factor, i) => {
      const barX = x(i);
      const barW = x.bandwidth();
      const barY = y(factor.varianceExplained);
      const barH = innerH - barY;

      g.append('rect')
        .attr('x', barX)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', Math.max(0, barH))
        .attr('fill', FACTOR_COLORS[i % FACTOR_COLORS.length])
        .attr('rx', 2)
        .attr('opacity', 0.85);

      // Percentage label above bar
      g.append('text')
        .attr('x', barX + barW / 2)
        .attr('y', barY - 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', FACTOR_COLORS[i % FACTOR_COLORS.length])
        .style('pointer-events', 'none')
        .text(`${(factor.varianceExplained * 100).toFixed(0)}%`);
    });

    // Scree line connecting bar tops
    const lineData = factors.map((factor, i) => ({
      x: x(i) + x.bandwidth() / 2,
      y: y(factor.varianceExplained),
    }));

    const line = d3.line()
      .x((d) => d.x)
      .y((d) => d.y);

    g.append('path')
      .datum(lineData)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // Dots at line points
    lineData.forEach((pt) => {
      g.append('circle')
        .attr('cx', pt.x)
        .attr('cy', pt.y)
        .attr('r', 3)
        .attr('fill', '#fff')
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1.5);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [factors]);

  if (!data || (!data.loadings && !data.factors)) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Factor Loadings</div>
          <div style={styles.subtitle}>Latent structure underlying price field correlations</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No factor analysis data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Factor Loadings</div>
        <div style={styles.subtitle}>Latent structure underlying price field correlations</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {factors.map((factor, i) => (
          <div key={factor.id} style={styles.legendItem}>
            <span style={{ ...styles.legendSwatch, background: FACTOR_COLORS[i % FACTOR_COLORS.length] }} />
            <span style={{ color: '#374151' }}>
              {factor.label} ({(factor.varianceExplained * 100).toFixed(0)}% var.)
            </span>
          </div>
        ))}
      </div>

      <div style={styles.chartRow}>
        {/* Main loadings chart */}
        <div style={styles.mainSection}>
          <div style={styles.sectionTitle}>Factor Loadings by Price Field</div>
          <div style={styles.container} ref={containerRef}>
            <svg ref={loadingsSvgRef} />
          </div>
        </div>

        {/* Side panel: communalities + scree plot */}
        <div style={styles.sideSection}>
          <div>
            <div style={styles.sectionTitle}>Communalities</div>
            <div style={styles.container}>
              <svg ref={communalitySvgRef} />
            </div>
          </div>
          <div>
            <div style={styles.sectionTitle}>Variance Explained</div>
            <div style={styles.container}>
              <svg ref={screeSvgRef} />
            </div>
          </div>
          {data.totalVarianceExplained != null && (
            <div style={styles.totalVariance}>
              <span>Total Variance Explained</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                {(data.totalVarianceExplained * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.field}
          </div>
          {tooltip.communalityOnly ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Communality</span>
              <span style={{ fontWeight: 600 }}>{tooltip.communality.toFixed(3)}</span>
            </div>
          ) : (
            <>
              {tooltip.allLoadings && tooltip.allLoadings.map((entry) => (
                <div
                  key={entry.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginBottom: '2px',
                  }}
                >
                  <span style={{ color: entry.color }}>{entry.label}</span>
                  <span style={{ fontWeight: 600 }}>{entry.value.toFixed(3)}</span>
                </div>
              ))}
              {tooltip.communality != null && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginTop: '4px',
                    paddingTop: '4px',
                    borderTop: '1px solid #e5e7eb',
                  }}
                >
                  <span style={{ color: '#6b7280' }}>Communality</span>
                  <span style={{ fontWeight: 600 }}>{tooltip.communality.toFixed(3)}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
